import type { AnamnesisSummary } from '@/features/anamneses/api'
import type { Appointment, Level } from '@/features/appointments/api'
import type { TreatmentPlanSummary } from '@/features/treatment-plans/api'
import { dateEyebrow, isoDateIn } from '@/lib/format'
import { routes } from '@/routes'

/**
 * As frases prontas do início (docs/areas/portal-do-paciente.md, "Início"): a linha de contagem,
 * cada item da linha do tempo (atendimento, ficha de saúde e plano encerrado, RN-POR-23) com o olho,
 * o nome, o contexto e a etiqueta, e a nota sobre a assinatura. Vocabulário da paciente (RN-POR-05):
 * nada de pendência da clínica.
 */

export const FILTERS = ['all', 'appointments', 'anamneses', 'treatmentPlans'] as const
export type Filter = (typeof FILTERS)[number]

export const FILTER_LABELS: Record<Filter, string> = {
  all: 'Tudo',
  appointments: 'Atendimentos',
  anamneses: 'Fichas de saúde',
  treatmentPlans: 'Planos',
}

export const SIGNATURES_NOTE =
  'Assinar pelo portal chega com a área de Assinaturas. Até lá, a clínica envia o link para assinar.'

export const CLINICS_NOTE = 'Seus dados ficam com cada clínica. Para corrigir, fale com ela.'

export const EMPTY = {
  all: {
    title: 'Nada por aqui ainda',
    description:
      'Quando uma clínica Westhetics registrar um atendimento ou uma ficha de saúde com este e-mail, ele aparece aqui.',
  },
  appointments: {
    title: 'Nenhum atendimento ainda',
    description:
      'Quando uma clínica Westhetics preparar os documentos de um atendimento seu, ele aparece aqui.',
  },
  anamneses: {
    title: 'Nenhuma ficha de saúde ainda',
    description: 'Quando você responder a ficha de saúde que uma clínica enviar, ela aparece aqui.',
  },
  treatmentPlans: {
    title: 'Nenhum plano de tratamento ainda',
    description:
      'Quando uma clínica Westhetics encerrar o plano de tratamento de um atendimento seu, ele aparece aqui.',
  },
} as const

function plural(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`
}

/** "2 clínicas · 3 atendimentos · 2 fichas de saúde", e os planos quando há algum. */
export function countsLine(home: {
  clinics: unknown[]
  counts: { appointments: number; anamneses: number; treatmentPlans: number }
}): string {
  return [
    plural(home.clinics.length, 'clínica', 'clínicas'),
    plural(home.counts.appointments, 'atendimento', 'atendimentos'),
    plural(home.counts.anamneses, 'ficha de saúde', 'fichas de saúde'),
    home.counts.treatmentPlans > 0
      ? plural(home.counts.treatmentPlans, 'plano de tratamento', 'planos de tratamento')
      : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

/** A etiqueta de um atendimento: o que depende dela primeiro, depois o que espera, por fim o feito. */
export function appointmentTag(a: Appointment): { label: string; level: Level } | null {
  const order: Level[] = ['critical', 'attention', 'waiting', 'done']
  for (const level of order) {
    const d = a.documents.find((doc) => doc.level === level)
    if (d) return { label: d.label, level: d.level }
  }
  return null
}

export type TimelineKind = 'appointment' | 'anamnesis' | 'treatmentPlan'

/** O tipo de item que cada segmento mostra. */
export const FILTER_KINDS: Record<Exclude<Filter, 'all'>, TimelineKind> = {
  appointments: 'appointment',
  anamneses: 'anamnesis',
  treatmentPlans: 'treatmentPlan',
}

export interface TimelineItem {
  key: string
  kind: TimelineKind
  /** `aaaa-mm-dd`, para ordenar. */
  date: string
  eyebrow: string
  title: string
  meta: string
  tag: { label: string; level: Level } | null
  href: string
}

function practitionerFirst(name: string | null | undefined): string | null {
  return name ? name : null
}

export function timelineOf(home: {
  appointments: Appointment[]
  anamneses: AnamnesisSummary[]
  treatmentPlans: TreatmentPlanSummary[]
}): TimelineItem[] {
  const appointments: TimelineItem[] = home.appointments.map((a) => ({
    key: `appointment:${a.id}`,
    kind: 'appointment',
    date: a.date,
    eyebrow: `${dateEyebrow(a.date)} · ${a.clinic.name}`,
    title: a.title,
    meta: [practitionerFirst(a.practitioner?.name), a.summary].filter(Boolean).join(' · '),
    tag: appointmentTag(a),
    href: routes.appointment(a.id),
  }))
  const anamneses: TimelineItem[] = home.anamneses.map((an) => {
    const date = isoDateIn(an.completedAt, an.clinic.timezone)
    return {
      key: `anamnesis:${an.id}`,
      kind: 'anamnesis',
      date,
      eyebrow: `${dateEyebrow(date)} · ${an.clinic.name}`,
      title: 'Ficha de saúde',
      meta: [
        an.procedureAtSendTime ? `para ${an.procedureAtSendTime.name}` : null,
        an.protocolNumber ? `protocolo ${an.protocolNumber}` : 'importada do sistema anterior',
      ]
        .filter(Boolean)
        .join(' · '),
      tag: { label: 'Respondida', level: 'done' },
      href: routes.anamnesis(an.id),
    }
  })
  // O plano entra na data do encerramento, quando virou registro (RN-POR-23).
  const plans: TimelineItem[] = home.treatmentPlans.map((plan) => {
    const date = isoDateIn(plan.closedAt, plan.clinic.timezone)
    return {
      key: `treatmentPlan:${plan.id}`,
      kind: 'treatmentPlan',
      date,
      eyebrow: `${dateEyebrow(date)} · ${plan.clinic.name}`,
      title: 'Plano de tratamento',
      meta: [
        plan.procedures.length > 0 ? plan.title : null,
        plan.summary,
        plan.protocolNumber ? `protocolo ${plan.protocolNumber}` : 'importado do sistema anterior',
      ]
        .filter(Boolean)
        .join(' · '),
      tag: { label: 'Encerrado', level: 'done' },
      href: routes.treatmentPlan(plan.id),
    }
  })
  return [...appointments, ...anamneses, ...plans].sort(
    (a, b) => b.date.localeCompare(a.date) || a.kind.localeCompare(b.kind),
  )
}

/** "Há mais 1 cadastro…" / "Há mais 2 cadastros…" com o seu documento em outro e-mail (RN-POR-21). */
export function otherEmailTitle(count: number): string {
  return `Há mais ${count} ${count === 1 ? 'cadastro' : 'cadastros'} com o seu documento em outro e-mail`
}
